import {
  ArchiveContractResponseSchema, ContractAttachmentDownloadTicketSchema, ContractListResponseSchema,
  ProjectListResponseSchema, ProjectSummarySchema, StageContractUploadResponseSchema,
  ProjectAccessProfileSchema,
  ProjectCreationOptionsResponseSchema, CreateProjectResponseSchema, VoidContractResponseSchema,
  UnlinkContractAttachmentResponseSchema,
  ReleaseContractAttachmentRetentionResponseSchema,
  ProjectMemberListResponseSchema, ProjectMemberOptionsResponseSchema, ProjectMemberWriteResponseSchema,
  ProjectWriteResponseSchema,
  type ArchiveContractResponse, type ContractListResponse, type ProjectListResponse,
  type ProjectSummary, type StageContractUploadResponse, type ProjectCreationOptionsResponse, type CreateProjectResponse,
  type ProjectAccessProfile,
  type VoidContractResponse,
  type UnlinkContractAttachmentResponse,
  type ReleaseContractAttachmentRetentionResponse,
  type ProjectMemberListResponse, type ProjectMemberOptionsResponse, type ProjectMemberWriteResponse,
  type ProjectDuty, type ProjectWriteResponse,
} from '@youkuai/contracts';

export class ApiRequestError extends Error {
  public constructor(public readonly status: number, public readonly code: string,
    public readonly userMessage?: string) {
    super(`请求失败（${status} · ${code}）`);
  }
}

type SessionExpiredListener = (token: string) => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => { sessionExpiredListeners.delete(listener); };
}

function notifyExpiredSession(status: number, token: string): void {
  if (status === 401) for (const listener of sessionExpiredListeners) listener(token);
}

export async function apiRequest(path: string, token: string, options?: RequestInit): Promise<unknown> {
  const response = await fetch(`/api${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', ...options?.headers },
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    notifyExpiredSession(response.status, token);
    throw responseError(response.status, payload);
  }
  return payload;
}

export async function apiDownload(path: string, token: string): Promise<Blob> {
  const response = await fetch(`/api${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) {
    notifyExpiredSession(response.status, token);
    const payload: unknown = await response.json().catch(() => null);
    throw responseError(response.status, payload);
  }
  return response.blob();
}

function responseError(status: number, payload: unknown): ApiRequestError {
  const code = payload && typeof payload === 'object' && 'code' in payload && typeof payload.code === 'string'
    ? payload.code : 'REQUEST_FAILED';
  const message = payload && typeof payload === 'object' && 'message' in payload && typeof payload.message === 'string'
    ? payload.message : undefined;
  return new ApiRequestError(status, code, message);
}

export async function listProjects(token: string, search = '', afterProjectCode?: string): Promise<ProjectListResponse> {
  const query = new URLSearchParams({ limit: '100' });
  if (search.trim()) query.set('search', search.trim());
  if (afterProjectCode) query.set('afterProjectCode', afterProjectCode);
  return ProjectListResponseSchema.parse(await apiRequest(`/projects?${query}`, token));
}

export async function listProjectCreationOptions(token: string, kind: 'customer' | 'manager', search = '',
  afterCode?: string): Promise<ProjectCreationOptionsResponse> {
  const query = new URLSearchParams({ kind, limit: '25' });
  if (search.trim()) query.set('search', search.trim());
  if (afterCode) query.set('afterCode', afterCode);
  return ProjectCreationOptionsResponseSchema.parse(await apiRequest(`/projects/creation-options?${query}`, token));
}

export async function createProject(input: { token: string; idempotencyKey: string; name: string;
  businessEntityId: string; primaryManagerId: string }): Promise<CreateProjectResponse> {
  const body = { expectedVersion: 0, businessEntityId: input.businessEntityId,
    name: input.name.trim(), primaryManagerId: input.primaryManagerId };
  return CreateProjectResponseSchema.parse(await apiRequest('/projects', input.token, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.idempotencyKey },
    body: JSON.stringify(body),
  }));
}

export async function getProject(token: string, projectId: string): Promise<ProjectSummary> {
  return ProjectSummarySchema.parse(await apiRequest(`/projects/${encodeURIComponent(projectId)}`, token));
}

export async function getProjectAccessProfile(token: string, projectId: string): Promise<ProjectAccessProfile> {
  return ProjectAccessProfileSchema.parse(await apiRequest(
    `/projects/${encodeURIComponent(projectId)}/access-profile`, token));
}

export async function listProjectMembers(token: string, projectId: string): Promise<ProjectMemberListResponse> {
  return ProjectMemberListResponseSchema.parse(await apiRequest(
    `/projects/${encodeURIComponent(projectId)}/members`, token));
}

export async function listProjectMemberOptions(token: string, projectId: string): Promise<ProjectMemberOptionsResponse> {
  return ProjectMemberOptionsResponseSchema.parse(await apiRequest(
    `/projects/${encodeURIComponent(projectId)}/member-options`, token));
}

export async function addProjectMember(input: { token: string; projectId: string; personId: string;
  duties: ProjectDuty[]; idempotencyKey: string }): Promise<ProjectMemberWriteResponse> {
  return ProjectMemberWriteResponseSchema.parse(await apiRequest(
    `/projects/${encodeURIComponent(input.projectId)}/members`, input.token, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.idempotencyKey },
      body: JSON.stringify({ personId: input.personId, duties: input.duties }),
    }));
}

export async function updateProjectMember(input: { token: string; projectId: string; membershipId: string;
  expectedVersion: number; duties: ProjectDuty[]; idempotencyKey: string }): Promise<ProjectMemberWriteResponse> {
  return ProjectMemberWriteResponseSchema.parse(await apiRequest(
    `/projects/${encodeURIComponent(input.projectId)}/members/${encodeURIComponent(input.membershipId)}`,
    input.token, { method: 'PUT', headers: { 'Content-Type': 'application/json',
      'Idempotency-Key': input.idempotencyKey }, body: JSON.stringify({ expectedVersion: input.expectedVersion,
      duties: input.duties }) }));
}

export async function endProjectMember(input: { token: string; projectId: string; membershipId: string;
  expectedVersion: number; idempotencyKey: string }): Promise<ProjectMemberWriteResponse> {
  return ProjectMemberWriteResponseSchema.parse(await apiRequest(
    `/projects/${encodeURIComponent(input.projectId)}/members/${encodeURIComponent(input.membershipId)}`,
    input.token, { method: 'DELETE', headers: { 'Content-Type': 'application/json',
      'Idempotency-Key': input.idempotencyKey }, body: JSON.stringify({ expectedVersion: input.expectedVersion }) }));
}

export async function updateProject(input: { token: string; projectId: string; expectedVersion: number;
  name: string; customerId: string; primaryManagerId: string; description: string | null; reason: string;
  idempotencyKey: string }): Promise<ProjectWriteResponse> {
  return ProjectWriteResponseSchema.parse(await apiRequest(`/projects/${encodeURIComponent(input.projectId)}`,
    input.token, { method: 'PUT', headers: { 'Content-Type': 'application/json',
      'Idempotency-Key': input.idempotencyKey }, body: JSON.stringify({ expectedVersion: input.expectedVersion,
      name: input.name, customerId: input.customerId, primaryManagerId: input.primaryManagerId,
      description: input.description, reason: input.reason }) }));
}

async function changeProjectLifecycle(input: { token: string; projectId: string; expectedVersion: number;
  reason: string; idempotencyKey: string }, action: 'suspend' | 'resume'): Promise<ProjectWriteResponse> {
  return ProjectWriteResponseSchema.parse(await apiRequest(
    `/projects/${encodeURIComponent(input.projectId)}/${action}`, input.token, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.idempotencyKey },
      body: JSON.stringify({ expectedVersion: input.expectedVersion, reason: input.reason }),
    }));
}

export function suspendProject(input: { token: string; projectId: string; expectedVersion: number;
  reason: string; idempotencyKey: string }): Promise<ProjectWriteResponse> {
  return changeProjectLifecycle(input, 'suspend');
}

export function resumeProject(input: { token: string; projectId: string; expectedVersion: number;
  reason: string; idempotencyKey: string }): Promise<ProjectWriteResponse> {
  return changeProjectLifecycle(input, 'resume');
}

export async function listContracts(token: string, projectId: string, afterCode?: string): Promise<ContractListResponse> {
  const query = new URLSearchParams({ limit: '100' });
  if (afterCode) query.set('afterCode', afterCode);
  return ContractListResponseSchema.parse(await apiRequest(`/projects/${encodeURIComponent(projectId)}/contracts?${query}`, token));
}

export async function exportContractAmounts(input: { token: string; projectId: string; purpose: string;
  afterCode: string | null; limit: number }): Promise<{ file: Blob; ticketId: string; rowCount: number }> {
  const root = `/projects/${encodeURIComponent(input.projectId)}/contracts/exports`;
  const issued = await apiRequest(root, input.token, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ purpose: input.purpose, afterCode: input.afterCode, limit: input.limit }),
  }) as { ticketId: string; rowCount: number; expiresAt: string; fileHash: string };
  if (!issued || typeof issued.ticketId !== 'string' || !/^\d+$/.test(String(issued.rowCount))) {
    throw new Error('导出票据响应无效');
  }
  const file = await apiDownload(`${root}/${encodeURIComponent(issued.ticketId)}`, input.token);
  return { file, ticketId: issued.ticketId, rowCount: issued.rowCount };
}

function declaredMime(file: File): string {
  if (['application/pdf', 'image/png', 'image/jpeg'].includes(file.type)) return file.type;
  const extension = file.name.split('.').at(-1)?.toLowerCase();
  if (extension === 'pdf') return 'application/pdf';
  if (extension === 'png') return 'image/png';
  if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
  return 'application/octet-stream';
}

export async function stageContractFile(input: {
  token: string; projectId: string; expectedVersion: number; file: File; idempotencyKey: string;
}): Promise<StageContractUploadResponse> {
  const query = new URLSearchParams({ filename: input.file.name, expectedVersion: String(input.expectedVersion) });
  const payload = await apiRequest(`/projects/${encodeURIComponent(input.projectId)}/contracts/uploads?${query}`,
    input.token, { method: 'POST', body: input.file, headers: {
      'Content-Type': declaredMime(input.file), 'Idempotency-Key': input.idempotencyKey,
    } });
  return StageContractUploadResponseSchema.parse(payload);
}

export interface ArchiveContractDraft {
  expectedVersion: number;
  name: string;
  kind: 'ORIGINAL' | 'SUPPLEMENT' | 'RENEWAL' | 'CORRECTION';
  parentContractId: string | null;
  counterpartyId: string;
  amountKind: 'OPEN' | 'FIXED';
  amount: string | null;
  signedOn: string | null;
  validFrom: string | null;
  validUntil: string | null;
  notes: string | null;
  stagingIds: string[];
  relatedProjectIds?: string[];
}

export async function archiveContract(input: {
  token: string; projectId: string; idempotencyKey: string; draft: ArchiveContractDraft;
}): Promise<ArchiveContractResponse> {
  const payload = await apiRequest(`/projects/${encodeURIComponent(input.projectId)}/contracts`, input.token, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.idempotencyKey },
    body: JSON.stringify(input.draft),
  });
  return ArchiveContractResponseSchema.parse(payload);
}

export async function voidContract(input: {
  token: string; projectId: string; contractId: string; idempotencyKey: string;
  expectedVersion: number; reason: string;
}): Promise<VoidContractResponse> {
  const payload = await apiRequest(`/projects/${encodeURIComponent(input.projectId)}/contracts/${encodeURIComponent(input.contractId)}/void`,
    input.token, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.idempotencyKey },
      body: JSON.stringify({ expectedVersion: input.expectedVersion, reason: input.reason.trim() }),
    });
  return VoidContractResponseSchema.parse(payload);
}

export async function unlinkContractAttachment(input: {
  token: string; projectId: string; contractId: string; attachmentId: string;
  idempotencyKey: string; expectedVersion: number; reason: string;
}): Promise<UnlinkContractAttachmentResponse> {
  const path = `/projects/${encodeURIComponent(input.projectId)}/contracts/${encodeURIComponent(input.contractId)}` +
    `/attachments/${encodeURIComponent(input.attachmentId)}/unlink`;
  return UnlinkContractAttachmentResponseSchema.parse(await apiRequest(path, input.token, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.idempotencyKey },
    body: JSON.stringify({ expectedVersion: input.expectedVersion, reason: input.reason.trim() }),
  }));
}

export async function releaseContractAttachmentRetention(input: {
  token: string; projectId: string; contractId: string; attachmentId: string;
  idempotencyKey: string; expectedVersion: number; reason: string;
}): Promise<ReleaseContractAttachmentRetentionResponse> {
  const path = `/projects/${encodeURIComponent(input.projectId)}/contracts/${encodeURIComponent(input.contractId)}` +
    `/attachments/${encodeURIComponent(input.attachmentId)}/retention-release`;
  return ReleaseContractAttachmentRetentionResponseSchema.parse(await apiRequest(path, input.token, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.idempotencyKey },
    body: JSON.stringify({ expectedVersion: input.expectedVersion, reason: input.reason.trim() }),
  }));
}

export async function fetchContractAttachment(input: {
  token: string; projectId: string; contractId: string; attachmentId: string;
}): Promise<Blob> {
  const base = `/projects/${encodeURIComponent(input.projectId)}/contracts/${encodeURIComponent(input.contractId)}/attachments/${encodeURIComponent(input.attachmentId)}`;
  const issued = ContractAttachmentDownloadTicketSchema.parse(
    await apiRequest(`${base}/download-ticket`, input.token, { method: 'POST' }),
  );
  const response = await fetch(`/api${base}/download?${new URLSearchParams({ ticket: issued.ticket })}`, {
    headers: { Authorization: `Bearer ${input.token}` }, cache: 'no-store',
  });
  if (!response.ok) {
    notifyExpiredSession(response.status, input.token);
    throw responseError(response.status, await response.json().catch(() => null));
  }
  return response.blob();
}
